import {faCheckCircle, faCircle, faEdit, faTrash, faUser} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {createUseStyles} from 'react-jss'
import {Colors} from "../../constants/colors"
import {CompanyGroupPortalRoleModel, CompanyGroupPortalUser} from "../../redux/types"
import Card from '../Card'
import {useTranslation} from 'react-i18next'

interface Props {
  companyGroupPortalRole: CompanyGroupPortalRoleModel
  companyGroupPortalUsers: CompanyGroupPortalUser[]
  onEditClick: (portalRole: CompanyGroupPortalRoleModel) => void
  onDeleteClick: (portalRole: CompanyGroupPortalRoleModel) => void
}

const CompanyGroupPortalRoleCard = ({companyGroupPortalRole, companyGroupPortalUsers, onEditClick, onDeleteClick}: Props) => {
  const styles = useStyles()
  const {t} = useTranslation(['common', 'portalRoles']);

  const usersInRole = companyGroupPortalUsers.filter(x => x.portalRoles.some(y => y.id == companyGroupPortalRole.id))

  const renderProperty = (propertyName: keyof CompanyGroupPortalRoleModel) => {
    const enabled = !!companyGroupPortalRole[propertyName];
    const name = propertyName as string;

    return (
      <div className={styles.propertyContainer}>
        <FontAwesomeIcon
          icon={enabled ? faCheckCircle : faCircle}
          color={enabled ? Colors.euro_800 : Colors.gray3}
        />
        <span>{t(name, {ns: 'portalRoles'})}</span>
      </div>
    );
  };

  const renderUser = (user: CompanyGroupPortalUser) => {
    return (
      <div key={user.portalUserId} className={styles.propertyContainer}>
        <FontAwesomeIcon
          icon={faUser}
          color={Colors.euro_800}
        />
        <span>{user.givenName} {user.familyName}</span>
        <span>{user.email}</span>
      </div>
    )
  }

  return (
    <Card className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 style={{color: Colors.euro_800}}>{companyGroupPortalRole.name}</h3>
        <div className={styles.toolbar}>
          <FontAwesomeIcon
            className={styles.button}
            icon={faEdit}
            color={Colors.gray1}
            title={t('edit', {ns: 'common'})}
            onClick={() => onEditClick(companyGroupPortalRole)}
          />
          <FontAwesomeIcon
            className={styles.button}
            icon={faTrash}
            color={Colors.gray1}
            title={t('delete', {ns: 'common'})}
            onClick={() => onDeleteClick(companyGroupPortalRole)}
          />
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.subCard}>
          <h4>{t('policies', {ns: 'portalRoles'})}</h4>

          <div className={styles.row}>
            {renderProperty("allowManageUsers")}
            {renderProperty("allowManagePayroll")}
            {renderProperty("allowManageEmployeeActivations")}
          </div>
        </div>

        <div className={styles.subCard} style={{backgroundColor: Colors.grayBackground}}>
          <h4>{t('portalUsers', {ns: 'portalRoles'})}</h4>

          <div className={styles.row}>
            {usersInRole.map(x => renderUser(x))}
          </div>
        </div>
      </div>
    </Card>
  )
}

export default CompanyGroupPortalRoleCard

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    gap: 10,
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    flex: 1,
    flexWrap: 'wrap'
  },
  row: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 4
  },
  subCard: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    borderRadius: 8,
    padding: 10,
    gap: 10
  },
  propertyContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center'
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10
  },
  button: {
    transition: '0.4s',
    '&:hover': {
      color: Colors.euro_600,
      cursor: 'pointer',
    }
  }
})
